<template>
  <material-card :title="title" :text="subtitle">
    <v-layout>
      <v-flex md8>
        <p>
          <v-chip
            v-if="application.state === 'ACCEPTED'"
            color="green"
            text-color="white"
            class="text-md-right"
            tabindex="-1"
          >
            {{ applicationStateText }}
          </v-chip>

          <v-chip
            v-else-if="application.state === 'REJECTED'"
            color="red"
            text-color="white"
            class="text-md-right"
            tabindex="-1"
          >
            {{ applicationStateText }}
          </v-chip>

          <v-chip v-else class="text-md-right" tabindex="-1">
            {{ applicationStateText }}
          </v-chip>

          <v-chip class="text-md-right" tabindex="-1">
            {{ application.physical ? 'Fizyczna i elektroniczna' : 'Tylko elektroniczna' }}
          </v-chip>
        </p>

        <div v-if="application.firstname || application.lastName">
          <p class="body-2">Imię i nazwisko</p>

          <p class="subheading">{{ application.firstName }} {{ application.lastName }}</p>
        </div>

        <div v-if="application.city">
          <p class="body-2">Adres zamieszkania</p>

          <p class="subheading">{{ application | address }}</p>
        </div>

        <div v-if="application.email">
          <p class="body-2">Adres e-mail</p>

          <p class="subheading">{{ application.email }}</p>
        </div>

        <div v-if="application.phoneNumber">
          <p class="body-2">Numer telefonu</p>

          <p class="subheading">{{ application.phoneNumber }}</p>
        </div>

        <div v-if="application.collectionPoint">
          <p class="body-2">Punkt odbioru</p>

          <p class="subheading">
            {{ application.collectionPoint.name }}
            <span v-if="application.collectionPoint.city">{{ application.collectionPoint | address }}</span>
          </p>
        </div>
        <div v-if="application.style">
          <p class="body-2">Wygląd karty</p>

          <p class="subheading">
            {{ application.style }}<br /><img :src="'/img/cardstyle/' + application.style + '.png'" width="100" />
          </p>
        </div>
      </v-flex>

      <v-flex md4>
        <v-layout>
          <v-flex xs6>
            <dl>
              <dt>Data złożenia</dt>

              <dd>{{ application.created | date('YYYY-MM-DD') }}</dd>
            </dl>
          </v-flex>

          <v-flex xs6>
            <dl>
              <dt>Obecna data ważności</dt>

              <dd v-if="application.card.endDate">{{ application.card.endDate | date('YYYY-MM-DD') }}</dd>

              <dd v-else>Bezterminowa</dd>
            </dl>
          </v-flex>
        </v-layout>

        <dl v-if="application.acceptDate">
          <dt>Data akceptacji</dt>

          <dd>{{ application.acceptDate | date('YYYY-MM-DD HH:mm') }}</dd>
        </dl>

        <dl v-if="application.rejectDate">
          <dt>Data odrzucenia</dt>

          <dd>{{ application.rejectDate | date('YYYY-MM-DD HH:mm') }}</dd>
        </dl>

        <dl v-if="application.decisionMakerProfile">
          <dt>Osoba {{ application.acceptDate ? 'akceptująca' : 'odrzucająca' }}</dt>

          <dd>
            <router-link :to="{ name: 'official', params: { id: application.decisionMakerProfile.userId } }">
              {{ application.decisionMakerProfile.firstName }} {{ application.decisionMakerProfile.lastName }}
            </router-link>
          </dd>
        </dl>

        <dl v-if="application.reason">
          <dt>Komentarz</dt>

          <dd>{{ application.reason }}</dd>
        </dl>
      </v-flex>
    </v-layout>

    <div class="mb-4">
      <p class="body-2">Załączniki</p>

      <div v-if="application.physicalAttachments">
        Wnioskodawca odstąpił od składania załączników w systemie i zadeklarował, że dostarczy je do punktu wydawania
        kart.
      </div>

      <files
        v-if="application.mediaObjects.length"
        :media-list="application.mediaObjects"
        :application-id="application.id"
        @delete="confirmDeleteMedia"
      />

      <div v-if="!application.mediaObjects.length && !application.physicalAttachments">Brak</div>
    </div>

    <v-layout slot="actions">
      <v-flex>
        <v-btn @click="$router.go(-1)">Wróć</v-btn>

        <check-permission
          v-if="!hideShowProfileBtn"
          permissions="PROFILE.GET"
          entity-type="profile"
          :entity-id="userId"
        >
          <v-btn @click="goToProfile">Zobacz profil</v-btn>
        </check-permission>
      </v-flex>

      <v-spacer />

      <check-permission permissions="APPLICATION.UPDATE">
        <v-flex shrink>
          <v-btn v-if="application.state === 'SUBMITTED'" dark color="red darken-2" @click="reject">
            <v-icon left>close</v-icon>Odrzuć wniosek
          </v-btn>

          <v-btn v-if="application.state === 'SUBMITTED'" dark color="blue darken-2" @click="accept">
            <v-icon left>check</v-icon>Akceptuj wniosek
          </v-btn>
        </v-flex>
      </check-permission>
    </v-layout>

    <status-change-modal
      :id="application.id"
      :show="modals.statusChange"
      :state="state"
      :new-card-application="newCardApplication"
      @close="closeModal('statusChange')"
    />

    <confirm-modal
      title="Potwierdź usunięcie pliku"
      :description="confirmMediaDeleteTitle"
      :show="modals.confirmMediaDelete"
      @confirm="deleteMedia"
      @close="closeModal('confirmMediaDelete')"
    />
  </material-card>
</template>

<script>
import { address, dateFilter, formatError } from '@witkac/kartamiejska-shared';
import { mapState } from 'vuex';

import { CONST } from '@/const';
import StatusChangeModal from '@/components/application/StatusChangeModal';
import Files from '@/components/files/Files';
import ConfirmModal from '@/components/helpers/ConfirmModal';
import MaterialCard from '@/components/material/Card';
import { displayModalMixin } from '@/mixins/displayModal';
import { notificationsMixin } from '@/mixins/notifications';
import { config } from '@/utils/config';

export default {
  components: { ConfirmModal, MaterialCard, Files, StatusChangeModal },
  filters: { address, date: dateFilter },
  mixins: [displayModalMixin, notificationsMixin],
  props: {
    application: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      required: true
    },
    applicationStateText: {
      type: String,
      required: true
    },
    hideShowProfileBtn: {
      type: Boolean,
      default: false
    },
    // Czy wniosek o nową kartę
    newCardApplication: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      mediaURL: config.mediaURL,
      state: undefined,
      mediaToDelete: undefined,
      cardStyles: CONST.cardStyles
    };
  },
  computed: {
    ...mapState({
      userId: state => state.application.data.profile.userId
    }),
    confirmMediaDeleteTitle() {
      return `Czy na pewno chcesz usunąć plik "${this.mediaToDelete && this.mediaToDelete.title}"?`;
    }
  },
  methods: {
    // @vuese
    // Funkcja przenosząca do profilu użytkownika
    goToProfile() {
      try {
        const id = this.userId;
        this.$router.push({ name: 'client', params: { id } });
      } catch (error) {
        this.notifyError('Nie znaleziono uczestnika.');
      }
    },
    // @vuese
    // Akceptowanie wniosku
    accept() {
      this.state = 'ACCEPTED';
      this.openModal('statusChange');
    },
    // @vuese
    // Odrzucanie wniosku
    reject() {
      this.state = 'REJECTED';
      this.openModal('statusChange');
    },
    // @vuese
    // Potwierdzenie usunięcia pliku
    confirmDeleteMedia(media) {
      this.mediaToDelete = media;
      this.openModal('confirmMediaDelete');
    },
    // @vuese
    // Usunięcie pliku
    deleteMedia() {
      this.$store
        .dispatch('application/removeMedia', { id: this.application.id, mediaId: this.mediaToDelete.id })
        .then(() => {
          this.notifySuccess('Plik został usunięty.');
          this.closeModal('confirmMediaDelete');
        })
        .catch(err => this.notifyError(formatError(err)));
    }
  }
};
</script>
